import React from 'react';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';
import Tooltip from '@src/components/Tooltip';

import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { FEATURE, PACKAGE_CHANGE_TYPES } from '@src/constants/package';
import { Typography, useMediaQuery } from '@mui/material';
import TypographyCustom from '@src/new-components/Typography';
import {
  checkCustomPackage,
  getPackageChangeType,
} from '@src/services/package';
import { useSelector } from 'react-redux';
import { COLOR } from '@src/styles/color';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import theme from '@src/styles/theme';

const Content = ({ usingPackage, packageState, creditsOrderData }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { user = {} } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const useNoteInfoPackage = getFeatureValue(FEATURE_KEYS.NOTE_INFO_PACKAGE, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const packageChangeType = getPackageChangeType({
    currPackage: packageState,
    prevPackage: usingPackage,
    prevPackageExpiredDate: user?.packageExpiryDate,
  });

  const isDowngrade = packageChangeType === PACKAGE_CHANGE_TYPES.DOWNGRADE;
  const isCustomPackage = checkCustomPackage({
    level: usingPackage.level,
    type: usingPackage.type,
  });

  const creditsTransferred = creditsOrderData?.creditsTransferred || 0;

  const getVoiceLevelContent = (currPkg, buyPkg) => {
    const isCurrPkgHasBasicGlobalVoice = currPkg?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isCurrPkgHasAdvanceGlobalVoice = currPkg?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );
    const isNewPkgHasBasicGlobalVoice = buyPkg?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isNewPkgHasAdvanceGlobalVoice = buyPkg?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );

    let voiceLevelContent = '';
    let voiceLevelStatus = 'error';

    const isNotSupportAdvancedGlobalVoice =
      isCurrPkgHasAdvanceGlobalVoice &&
      !isNewPkgHasAdvanceGlobalVoice &&
      isNewPkgHasBasicGlobalVoice;

    const isNotSupportBasicAndAdvanceGlobalVoice =
      isCurrPkgHasAdvanceGlobalVoice &&
      !isNewPkgHasAdvanceGlobalVoice &&
      !isNewPkgHasBasicGlobalVoice;

    const isNotSupportBasicGlobalVoice =
      isCurrPkgHasBasicGlobalVoice && !isNewPkgHasBasicGlobalVoice;

    const notChange =
      !isCurrPkgHasBasicGlobalVoice && !isNewPkgHasBasicGlobalVoice;
    if (notChange) return null;

    if (isNotSupportAdvancedGlobalVoice) {
      voiceLevelContent = t('notSupportAdvancedGlobalVoiceV3');
      voiceLevelStatus = 'error';
    } else if (isNotSupportBasicAndAdvanceGlobalVoice) {
      voiceLevelContent = t('notSupportBasicAndAdvancedGlobalVoiceV3');
      voiceLevelStatus = 'error';
    } else if (isNotSupportBasicGlobalVoice) {
      voiceLevelContent = t('notSupportBasicGlobalVoiceV3');
      voiceLevelStatus = 'error';
    }
    if (!voiceLevelContent) return null;

    return { content: voiceLevelContent, status: voiceLevelStatus };
  };

  const getVoiceCloningContent = (currPkg, buyPkg) => {
    // Extract voice cloning properties
    const voiceCloning = {
      current: {
        hasFeature: currPkg?.features?.includes(FEATURE.AI_VOICE_CLONING),
        voices: currPkg?.maxCloningVoices || 0,
      },
      new: {
        hasFeature: buyPkg?.features?.includes(FEATURE.AI_VOICE_CLONING),
        voices: buyPkg?.maxCloningVoices || 0,
      },
    };

    // Handle case where feature is being removed
    if (voiceCloning.current.hasFeature && !voiceCloning.new.hasFeature) {
      return {
        content: t('noAccessToVoiceCloningAIV3'),
        status: 'error',
      };
    }

    return null;
  };

  const getTotalQuotaContent = () => {
    if (creditsTransferred > 0) {
      return {
        content: t('charsWillBeForwardedToYourNewPackage', {
          credits: creditsTransferred.toLocaleString(language),
        }),
        status: 'success',
      };
    }
    return null;
  };

  const getLimitCreditsPerMonthContent = (buyPkg) => {
    const totalCycles = buyPkg?.cycle?.totalCycles || 0;
    if (totalCycles > 1 && isCustomPackage) {
      return {
        content: t('limitCreditsPerMonthV2'),
        status: 'error',
      };
    }
    return null;
  };

  const getQuotaChangeContent = () => {
    let quotaChangeQuotaSingleCycleContent = '';
    let quotaChangeQuotaSingleCycleStatus = 'error';
    const note = '';

    if (isDowngrade) {
      quotaChangeQuotaSingleCycleContent = t('amountOfCharactersWillBeLostV2', {
        credits: creditsOrderData?.creditsLost?.toLocaleString(language),
      });
      quotaChangeQuotaSingleCycleStatus = 'error';
    }
    return {
      content: quotaChangeQuotaSingleCycleContent,
      status: quotaChangeQuotaSingleCycleStatus,
      note,
    };
  };

  const getPackageChangeContent = (currPkg, buyPkg) => {
    const contents = [];

    const quotaSingleCycleContent = getQuotaChangeContent();
    if (quotaSingleCycleContent) contents.push(quotaSingleCycleContent);

    const voiceLevelContent = getVoiceLevelContent(currPkg, buyPkg);
    if (voiceLevelContent) contents.push(voiceLevelContent);

    const voiceCloningAIContent = getVoiceCloningContent(currPkg, buyPkg);
    if (voiceCloningAIContent) contents.push(voiceCloningAIContent);

    const limitCreditsPerMonthContent = getLimitCreditsPerMonthContent(buyPkg);
    if (limitCreditsPerMonthContent) contents.push(limitCreditsPerMonthContent);

    const totalQuotaContent = getTotalQuotaContent();
    if (totalQuotaContent) contents.push(totalQuotaContent);

    return contents;
  };

  const changePackageContents = getPackageChangeContent(
    usingPackage,
    packageState,
  );

  const renderStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleRoundedIcon className="icon-check-circle" />;
      case 'error':
        return <CancelRoundedIcon className="icon-cancel-circle" />;
      case 'warning':
        return <ErrorRoundedIcon className="icon-error-circle" />;
      default:
        return <CheckCircleRoundedIcon className="icon-error-circle" />;
    }
  };

  const renderNoteMobile = (items) => {
    const note = items?.find((item) => item?.note);
    if (!note) return null;

    return (
      <div style={{ marginTop: '12px', fontSize: '14px' }}>
        (*){' '}
        <TypographyCustom
          variant="body-14-regular"
          color={COLOR.darkGray}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(note?.note),
          }}
          textTransform="none"
        />
      </div>
    );
  };

  const items = changePackageContents?.filter((item) => item?.content);
  const showNote = isCustomPackage && useNoteInfoPackage;

  return (
    <div className="list">
      {items?.map((item) => (
        <div
          key={item?.key}
          className="item"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {renderStatusIcon(item?.status)}
          <Typography
            component="div"
            dangerouslySetInnerHTML={{
              __html: item?.content,
            }}
          />
          {item?.note &&
            showNote &&
            (!isMobile ? (
              <Tooltip title={item?.note} arrow placement="top">
                <InfoRoundedIcon
                  sx={{ color: COLOR.navyBlue, fontSize: '16px' }}
                  className="icon-info"
                />
              </Tooltip>
            ) : (
              <>(*)</>
            ))}
        </div>
      ))}
      {isMobile && showNote && renderNoteMobile(items)}
    </div>
  );
};

export default Content;
